import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { REDUCED_MOTION_MEDIA_QUERY } from "../../config/constants";

import * as styles from "./styles.module.scss";

// In milliseconds; also sets the fade duration in the styles.
const DURATION = 200;

/**
 * Whether the user asked for reduced motion.
 *
 * @returns {boolean} Whether motion should be reduced.
 */
const prefersReducedMotion = () => Boolean( window.matchMedia && window.matchMedia( REDUCED_MOTION_MEDIA_QUERY ).matches );

/**
 * Gets the default crossfade key: the pathname.
 *
 * @param {Object} location The location.
 *
 * @returns {string} The key.
 */
const getPathname = ( location ) => location.pathname;

/**
 * Crossfades its content when the location changes to one with a different key.
 *
 * The outgoing content stays mounted, rendered for the previous location, on top of the incoming content until it has
 * faded out, while the height animates from the outgoing content's to the incoming content's. Children must render for
 * the location they get, not the current one, e.g. through `<Switch location>`.
 *
 * The incoming content comes first in the DOM, so labels and lookups by an id that both contents share reach it.
 *
 * @param {Object}   props                The props.
 * @param {Function} props.children       Gets a location and returns the content for it.
 * @param {Function} props.getKey         Gets a location and returns its key; only a key change crossfades.
 * @param {string}   props.layerClassName Class name for the outgoing and the incoming content's wrappers.
 *
 * @returns {ReactElement} The crossfading content.
 */
const RouteCrossfade = ( { children, getKey = getPathname, layerClassName = "" } ) => {
	const location                = useLocation();
	const [ shown, setShown ]     = useState( location );
	const [ leaving, setLeaving ] = useState( null );
	const wrapperRef              = useRef( null );
	const leavingRef              = useRef( null );
	const enteringRef             = useRef( null );

	if ( location !== shown ) {
		setShown( location );

		if ( getKey( location ) !== getKey( shown ) ) {
			setLeaving( prefersReducedMotion() ? null : shown );
		}
	}

	useLayoutEffect( () => {
		const wrapper = wrapperRef.current;
		if ( ! leaving || ! wrapper.animate ) {
			return;
		}

		const from = leavingRef.current.offsetHeight;
		const to   = enteringRef.current.offsetHeight;
		if ( from === to ) {
			return;
		}

		const animation = wrapper.animate(
			[ { height: `${ from }px`, overflow: "hidden" }, { height: `${ to }px`, overflow: "hidden" } ],
			{ duration: DURATION, easing: "ease" },
		);

		return () => animation.cancel();
	}, [ leaving ] );

	/**
	 * Removes the outgoing content once its own fade-out has ended.
	 *
	 * @param {AnimationEvent} event The animation event, which may bubble up from the content.
	 *
	 * @returns {void}
	 */
	const handleLeft = ( event ) => {
		if ( event.target === event.currentTarget ) {
			setLeaving( null );
		}
	};

	return (
		<div ref={ wrapperRef } className={ styles.crossfade } style={ { "--route-crossfade-duration": `${ DURATION }ms` } }>
			<div key={ getKey( location ) } ref={ enteringRef } className={ classNames( layerClassName, leaving && styles.entering ) || undefined }>
				{ children( location ) }
			</div>
			{ leaving && (
				<div
					key={ getKey( leaving ) }
					ref={ leavingRef }
					className={ classNames( layerClassName, styles.leaving ) }
					inert=""
					aria-hidden="true"
					onAnimationEnd={ handleLeft }
				>
					{ children( leaving ) }
				</div>
			) }
		</div>
	);
};

RouteCrossfade.propTypes = {
	children: PropTypes.func.isRequired,
	getKey: PropTypes.func,
	layerClassName: PropTypes.string,
};

export default RouteCrossfade;
